import { useState } from 'react'
import { Outlet } from 'react-router-dom'
import { useBrand } from '@proaxiv/brands'
import { Sidebar } from './Sidebar'
import { Header } from './Header'

/** Root layout shell: fixed sidebar + fixed header + scrollable content with brand background. */
export function AppLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const brand = useBrand()
  const isTms = brand.hostname === 'app.triploms.com'
  const isJppa = brand.hostname === 'app.jppa.triploms.com'
  const bgImage = isJppa
    ? '/brands/jppa/bg.png'
    : isTms
      ? '/brands/tms/bg.png'
      : '/brands/pax/bg.png'

  return (
    <div className="bg-pax-light-purple relative z-0 min-h-screen">
      {/* Decorative brand background — pinned to the viewport (not the growing content
          height) so it never stretches/shifts when accordions, dropdowns, or expanding
          rows change the page's layout height.
          `transform-gpu` forces this onto its own GPU-composited layer: without it, mobile
          Chrome/Safari can visibly detach and drift `position: fixed` layers for a frame or
          two while their address bar animates away during a scroll gesture, making the
          background appear to shift slightly before snapping back. */}
      <div
        className="pointer-events-none fixed inset-0 -z-10 transform-gpu overflow-hidden"
        aria-hidden="true"
      >
        <img
          src={bgImage}
          alt=""
          className={[
            'absolute inset-0 h-full w-full object-cover select-none',
            // PROAXIV: match the vivid, full-opacity look of the Keycloak login page's
            // background (same asset), which stays anchored bottom-right at every viewport
            // width instead of re-centering on larger screens. TMS keeps its original,
            // more muted treatment untouched.
            isTms || isJppa
              ? 'object-right opacity-60 sm:object-center'
              : 'object-right opacity-100',
          ].join(' ')}
        />
      </div>

      <Sidebar
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        onToggle={() => setSidebarOpen((o) => !o)}
      />
      <Header sidebarOpen={sidebarOpen} />

      {/* Main content
          Mobile (< md): always clears the persistent icon rail; the expanded drawer overlays
          content instead of pushing it (so the burger / rail stay put)
          Desktop (>= md): shifts right to clear the persistent sidebar rail */}
      <main
        className={[
          'relative min-h-screen pt-16 transition-[margin-left] duration-200',
          'ml-11',
          sidebarOpen ? 'md:ml-[260px]' : 'md:ml-[60px]',
        ].join(' ')}
      >
        {/* Page content sits above the decorative layer */}
        <div className="relative z-10">
          <Outlet />
        </div>
      </main>
    </div>
  )
}
